<script setup>
import { shallowRef, computed, defineProps, defineEmits } from "vue";
import { useMapStore } from "@/store/map.js";
import { useOrderStatus } from "@/composables/useOrderStatus";
const isExpanded = shallowRef(false);
const showTooltipProblem = shallowRef(false);
const showTooltipInfo = shallowRef(false);
const showTooltipPlate = shallowRef(false);
const mapStore = useMapStore();

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
});

const carColor = computed(() => {
  const val = props.order.truck.status;
  return val && val.trim() !== '' ? val : '#4D5460';
});

const lastSyncTimestamp = computed(() => {
  const val = props.order.truck.lastSyncTime;
  return val != null && val !== '' ? val : '-';
});

const lastMsgSat = computed(() => {
  const val = props.order.truck.satellites;
  return val != null && val !== '' ? val : '-';
});

const estimatedDuration = computed(() => {
  const val = props.order.estimatedDuration;
  return val != null ? val : '';
});

const estimatedDeliveryDate = computed(() => {
  const val = props.order.estimatedDeliveryDate;
  return val != null ? val : '';
});

const showEta = computed(() =>
    ['started_loading', 'finished_loading'].includes(props.order.status)
);

const showLeftLoading = computed(() =>
    ['finished_loading', 'is_delivered'].includes(props.order.status)
);

const showArrivedUnloading = computed(() =>
    ['is_delivered'].includes(props.order.status)
);

const { getStatusText, getOrderStatusColor } = useOrderStatus();
const emit = defineEmits(["show-more", "update:status"]);

const showRoute = () => {
  mapStore
    .calculateRoute(props.order)
    .then(() => {
      console.log("Route calculated successfully");
    })
    .catch((error) => {
      console.error("Error calculating route:", error);
    });
};

const tooltipMessage = computed(() => {
  const val = props.order.problem;
  return val != null ? val : '';
});

const showMore = () => {
  emit("show-more", props.order);
};

const toggleExpand = (event) => {
  // Проверяем, не произошел ли клик на элементах, которые мы хотим исключить
  const target = event.target;
  const clickedOnExcludedElement =
    target.closest(".order-drop") ||
    target.closest(".custom-dropdown__option") ||
    target.closest(".info-icon") ||
    target.closest(".status-icon") ||
    target.closest(".license-plate");

  if (!clickedOnExcludedElement) {
    isExpanded.value = !isExpanded.value;
  }
};
</script>

<template>
  <div
    class="order-item"
    @click="toggleExpand"
    v-memo="[
        isExpanded,
        showTooltipProblem,
        showTooltipInfo,
        showTooltipPlate,
        props.order
    ]"
  >
    <div class="order-header">
      <div class="order-number">№ {{ props.order.name }}</div>
      <div class="order-header_icons">
        <span :style="getOrderStatusColor(props.order.status)" class="status-text">{{ getStatusText(props.order.status) }}</span>
        <div
          class="status-icon"
          v-if="props.order.problem"
          @mouseenter="showTooltipProblem = true"
          @mouseleave="showTooltipProblem = false"
        >
          <div class="custom-tooltip problem-tooltip" v-show="showTooltipProblem">
            <div class="tooltip-content">{{ tooltipMessage }}</div>
          </div>
          <svg
            width="16"
            height="15"
            viewBox="0 0 16 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.70674 0.846669L0.216645 12.3639C-0.408117 13.4726 0.392993 14.8435 1.6656 14.8435H14.3345C15.5919 14.8435 16.3945 13.5021 15.8001 12.3941L9.62127 0.876908C9.00137 -0.278577 7.35049 -0.295705 6.70674 0.846669ZM8.71744 5.3052C8.72606 4.90378 8.40301 4.57372 8.0015 4.57372C7.60016 4.57372 7.27718 4.90349 7.28554 5.30474L7.3786 9.77413C7.38564 10.1123 7.6618 10.3828 8.00004 10.3828C8.33813 10.3828 8.61422 10.1125 8.62147 9.77453L8.71744 5.3052ZM7.41017 12.6325C7.57397 12.8039 7.77053 12.8896 7.99984 12.8896C8.15104 12.8896 8.28838 12.8507 8.41186 12.7731C8.53785 12.6927 8.63865 12.5856 8.71425 12.4517C8.79237 12.3178 8.83143 12.1691 8.83143 12.0058C8.83143 11.7647 8.74827 11.5585 8.58195 11.3871C8.41816 11.2157 8.22412 11.13 7.99984 11.13C7.77053 11.13 7.57397 11.2157 7.41017 11.3871C7.2489 11.5585 7.16826 11.7647 7.16826 12.0058C7.16826 12.2522 7.2489 12.4611 7.41017 12.6325Z"
              fill="#EB5E5E"
            />
          </svg>
        </div>
        <div
          class="info-icon"
          @click.stop="showMore()"
          @mouseenter="showTooltipInfo = true"
          @mouseleave="showTooltipInfo = false"
        >
          <div class="custom-tooltip info-tooltip" v-show="showTooltipInfo">
            <div class="tooltip-content">Детальная информация</div>
          </div>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 16C6.41775 16 4.87103 15.5308 3.55544 14.6518C2.23985 13.7727 1.21447 12.5233 0.608967 11.0615C0.00346629 9.59966 -0.15496 7.99113 0.153721 6.43928C0.462403 4.88743 1.22433 3.46197 2.34315 2.34315C3.46197 1.22433 4.88743 0.462403 6.43928 0.153721C7.99113 -0.15496 9.59966 0.00346629 11.0615 0.608967C12.5233 1.21447 13.7727 2.23985 14.6518 3.55544C15.5308 4.87103 16 6.41775 16 8C15.9977 10.121 15.1541 12.1545 13.6543 13.6543C12.1545 15.1541 10.121 15.9977 8 16V16ZM8 1.33334C6.68146 1.33334 5.39253 1.72433 4.2962 2.45687C3.19987 3.18942 2.34539 4.23061 1.84081 5.44878C1.33622 6.66695 1.2042 8.0074 1.46144 9.30061C1.71867 10.5938 2.35361 11.7817 3.28596 12.714C4.21831 13.6464 5.4062 14.2813 6.6994 14.5386C7.99261 14.7958 9.33305 14.6638 10.5512 14.1592C11.7694 13.6546 12.8106 12.8001 13.5431 11.7038C14.2757 10.6075 14.6667 9.31855 14.6667 8C14.6647 6.23249 13.9617 4.53792 12.7119 3.2881C11.4621 2.03828 9.76752 1.33528 8 1.33334V1.33334Z"
              fill="#A6A9AF"
            />
            <path
              d="M9.33365 12.667H8.00032V8.00033H6.66699V6.66699H8.00032C8.35394 6.66699 8.69308 6.80747 8.94313 7.05752C9.19318 7.30757 9.33365 7.6467 9.33365 8.00033V12.667Z"
              fill="#A6A9AF"
            />
            <path
              d="M8 5.33301C8.55228 5.33301 9 4.88529 9 4.33301C9 3.78072 8.55228 3.33301 8 3.33301C7.44772 3.33301 7 3.78072 7 4.33301C7 4.88529 7.44772 5.33301 8 5.33301Z"
              fill="#A6A9AF"
            />
          </svg>
        </div>
      </div>
    </div>
    <div class="company">
      {{ props.order.carrier }}
    </div>
    <div class="order-change">
      <div
        class="license-plate"
        @click.stop="showRoute"
        @mouseenter="showTooltipPlate = true"
        @mouseleave="showTooltipPlate = false"
      >
        <div class="custom-tooltip tooltip-right" v-show="showTooltipPlate">
          <div class="tooltip-content">
            Последнее сообщение: {{ lastSyncTimestamp }} Захвачено спутников:
            {{ lastMsgSat }}
          </div>
        </div>
        <span class="icon">
          <svg
            width="18"
            height="20"
            viewBox="0 0 18 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_444_10051)">
              <path
                d="M13.8206 4.16634H12.3658V3.33301C12.3658 2.66997 12.1359 2.03408 11.7267 1.56524C11.3174 1.0964 10.7624 0.833008 10.1836 0.833008H2.1822C1.60345 0.833008 1.0484 1.0964 0.639153 1.56524C0.22991 2.03408 0 2.66997 0 3.33301L0 15.833H1.48463C1.46591 15.9709 1.45594 16.1101 1.4548 16.2497C1.4548 17.0232 1.72303 17.7651 2.20048 18.3121C2.67793 18.8591 3.32549 19.1663 4.00071 19.1663C4.67592 19.1663 5.32348 18.8591 5.80093 18.3121C6.27838 17.7651 6.54661 17.0232 6.54661 16.2497C6.54547 16.1101 6.5355 15.9709 6.51679 15.833H10.9408C10.9221 15.9709 10.9122 16.1101 10.911 16.2497C10.911 17.0232 11.1792 17.7651 11.6567 18.3121C12.1341 18.8591 12.7817 19.1663 13.4569 19.1663C14.1321 19.1663 14.7797 18.8591 15.2571 18.3121C15.7346 17.7651 16.0028 17.0232 16.0028 16.2497C16.0017 16.1101 15.9917 15.9709 15.973 15.833H17.4576V8.33301C17.4565 7.22834 17.0729 6.16931 16.3911 5.38819C15.7093 4.60708 14.7849 4.16766 13.8206 4.16634ZM13.8206 5.83301C14.3994 5.83301 14.9544 6.0964 15.3637 6.56524C15.7729 7.03408 16.0028 7.66997 16.0028 8.33301V9.16634H12.3658V5.83301H13.8206ZM5.09181 16.2497C5.09181 16.5812 4.97685 16.8991 4.77223 17.1336C4.56761 17.368 4.29008 17.4997 4.00071 17.4997C3.71133 17.4997 3.4338 17.368 3.22918 17.1336C3.02456 16.8991 2.9096 16.5812 2.9096 16.2497C2.91005 16.1072 2.93297 15.9661 2.97725 15.833H5.02416C5.06844 15.9661 5.09136 16.1072 5.09181 16.2497ZM10.911 14.1663H1.4548V3.33301C1.4548 3.11199 1.53144 2.90003 1.66785 2.74375C1.80427 2.58747 1.98928 2.49967 2.1822 2.49967H10.1836C10.3765 2.49967 10.5616 2.58747 10.698 2.74375C10.8344 2.90003 10.911 3.11199 10.911 3.33301V14.1663ZM14.548 16.2497C14.548 16.5812 14.4331 16.8991 14.2284 17.1336C14.0238 17.368 13.7463 17.4997 13.4569 17.4997C13.1675 17.4997 12.89 17.368 12.6854 17.1336C12.4808 16.8991 12.3658 16.5812 12.3658 16.2497C12.3661 16.1072 12.3891 15.966 12.4335 15.833H14.4804C14.5248 15.966 14.5477 16.1072 14.548 16.2497ZM12.3658 14.1663V10.833H16.0028V14.1663H12.3658Z"
                :fill="carColor"
              />
              <path
                d="M10.911 14.1663H1.4548V3.33301C1.4548 3.11199 1.53144 2.90003 1.66785 2.74375C1.80427 2.58747 1.98928 2.49967 2.1822 2.49967H10.1836C10.3765 2.49967 10.5616 2.58747 10.698 2.74375C10.8344 2.90003 10.911 3.11199 10.911 3.33301V14.1663Z"
                :fill="carColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_444_10051">
                <rect width="17.4576" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </span>
        {{ props.order.truck.number }}
      </div>
    </div>

    <div class="other" v-show="isExpanded">
      <template v-if="showEta">
        <div class="divider"></div>
        <div class="time-info">
          <div>Прибудет</div>
          <div>{{ estimatedDeliveryDate }}</div>
        </div>
        <div class="divider"></div>
        <div class="time-info">
          <div>Расчетное время в пути</div>
          <div>{{ estimatedDuration }}</div>
        </div>
      </template>
      <div class="divider"></div>
      <div class="time-info">
        <div>Прибыл на&nbsp;погрузку</div>
        <div>{{ props.order.loadingArrivalTime }}</div>
      </div>
      <div class="divider"></div>
      <div class="time-info">
        <div>Время погрузки</div>
        <div>{{ props.order.loadingTime }}</div>
      </div>
      <template v-if="showLeftLoading">
        <div class="divider"></div>
        <div class="time-info">
          <div>Выехал с&nbsp;погрузки</div>
          <div>{{ props.order.loadingDepartureTime }}</div>
        </div>
      </template>
      <template v-if="showArrivedUnloading">
        <div class="divider"></div>
        <div class="time-info">
          <div>Прибыл на&nbsp;разгрузку</div>
          <div>{{ props.order.deliveryArrivalTime }}</div>
        </div>
      </template>
      <div class="divider"></div>
      <div class="driver-phone">
        <span>{{ props.order.driver }}</span>
      </div>
      <div class="divider"></div>
      <div class="info-with-icon">
        <div class="info-label">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            enable-background="new 0 0 512.003 512.003"
            width="16"
            height="16"
          >
            <path
              d="m15.963 14.732 -2.5 -9a1 1 0 0 0 -0.964 -0.733h-3.5v-0.212a2.512 2.512 0 0 0 1.5 -2.288c0 -1.378 -1.122 -2.5 -2.5 -2.5S5.499 1.121 5.499 2.499c0 1.022 0.625 1.902 1.5 2.288v0.212H3.499a1 1 0 0 0 -0.964 0.733L0.035 14.732a1 1 0 0 0 0.167 0.873 1 1 0 0 0 0.796 0.394h14c0.313 0 0.607 -0.146 0.796 -0.394a1 1 0 0 0 0.167 -0.873M8 2a0.5 0.5 0 1 1 0 1 0.5 0.5 0 0 1 0 -1"
            />
          </svg>
          <span>Вес</span>
        </div>
        <div class="info-value">{{ props.order.weight }}</div>
      </div>
      <div class="divider"></div>
      <div class="info-with-icon">
        <div class="info-label">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g>
              <rect x="1" y="3" width="1" height="10" fill="currentColor" />
              <rect x="3" y="3" width="2" height="10" fill="currentColor" />
              <rect x="6" y="3" width="1" height="10" fill="currentColor" />
              <rect x="8" y="3" width="1" height="10" fill="currentColor" />
              <rect x="10" y="3" width="2" height="10" fill="currentColor" />
              <rect x="13" y="3" width="1" height="10" fill="currentColor" />
            </g>
          </svg>
          <span>Материал</span>
        </div>
        <div class="info-value">{{ props.order.cargoBrand }}</div>
      </div>
      <div class="divider"></div>
      <div class="info-with-icon">
        <div class="info-label">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            enable-background="new 0 0 512.003 512.003"
            width="16"
            height="16"
          >
            <path
              d="m15.963 14.732 -2.5 -9a1 1 0 0 0 -0.964 -0.733h-3.5v-0.212a2.512 2.512 0 0 0 1.5 -2.288c0 -1.378 -1.122 -2.5 -2.5 -2.5S5.499 1.121 5.499 2.499c0 1.022 0.625 1.902 1.5 2.288v0.212H3.499a1 1 0 0 0 -0.964 0.733L0.035 14.732a1 1 0 0 0 0.167 0.873 1 1 0 0 0 0.796 0.394h14c0.313 0 0.607 -0.146 0.796 -0.394a1 1 0 0 0 0.167 -0.873M8 2a0.5 0.5 0 1 1 0 1 0.5 0.5 0 0 1 0 -1"
            />
          </svg>
          <span>Фактический вес</span>
        </div>
        <div class="info-value">{{ props.order.weight }}</div>
      </div>
      <div class="divider"></div>
      <div class="sender">
        <span>{{ props.order.carrier }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.order-item {
  // background-color: var(--card-bg);
  background-color: #f6f6f6;
  border-radius: 8px;
  padding: 14px;
  cursor: pointer;
  width: 317px;
  min-width: 317px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  height: 16px;

  &_icons {
    display: flex;
    column-gap: 8px;
    align-items: center;
  }
}

.info-icon {
  position: relative;
  cursor: pointer;
  display: flex;
  height: 16px;
}

.order-number {
  font-weight: 600;
  // color: var(--text-color);
  color: #4d5460 !important;
  font-size: 16px;
  line-height: 16px;
  @media screen and (max-width: 1024px) {
    font-size: 14px;
  }
}
.info-with-icon {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  line-height: 1.5;
  color: #4d5460;
  gap: 0 8px;
  padding: 4px 0;

  .info-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 400;
    flex-shrink: 0;

    svg {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    span {
      color: #4d5460;
    }
  }

  .info-value {
    color: #4d5460;
    font-weight: 600;
    text-align: left;
    flex-grow: 1;
  }
}
.time-info-left {
  display: flex;
  width: 160px !important;
  align-items: center;
  gap: 8px;
}
.order-drop {
  border-radius: 6px;
  :deep(.custom-dropdown__selected) {
    border: none;
  }
  &__operator {
    border-radius: 6px;
    // background: var(--body-color);
    background: #fff;
    // color: var(--text-color);
    color: #4d5460 !important;
    padding: 8px 12px;
    font-size: 14px;
    height: 36px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.company {
  font-weight: 600;
  color: #4d5460;
  line-height: 16px;
  // color: var(--text-color);
  font-size: 16px;
  margin-bottom: 8px;
  @media screen and (max-width: 1024px) {
    font-size: 14px;
  }
}

.status-text {
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  // color: var(--text-color);
  color: #4d5460;
}

.order-change {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-icon {
  position: relative;
  cursor: pointer;
  display: flex;
  height: 16px;
}

.custom-tooltip {
  position: absolute;
  left: auto;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  background-color: #000000;
  color: #ffffff;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  z-index: 100; /* Increased z-index to ensure it appears above other elements */
  width: max-content;
  max-width: 150px; /* Increased max-width to accommodate longer messages */
  margin-right: 10px;
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.4;
  text-align: left;
  pointer-events: none; /* Prevents tooltip from interfering with mouse events */

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 6px solid #000000;
  }
}

.order-item:first-child .problem-tooltip,
.order-item:first-child .info-tooltip {
  transform: translateY(0);
  top: -5px;

  &:after {
    top: 8px;
    transform: translateY(0);
  }
}

.tooltip-right {
  right: auto;
  left: 100%;
  margin-left: 10px;
  margin-right: 0;
  z-index: 10;
  pointer-events: none;

  &:after {
    content: "";
    position: absolute;
    top: 50%;
    right: 100%;
    left: auto;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid #000000;
    border-left: none;
  }
}

.tooltip-content {
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.5;
  text-align: left;
  width: 100%;
}

.license-plate {
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  // background: var(--body-color);
  background: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  // color: var(--text-color);
  color: #4d5460;
  max-width: 135px;

  .icon {
    position: relative;
    display: inline-block;
    margin-right: 5px;
    cursor: pointer;
    z-index: 5;
  }
}

.other {
  margin-top: 8px;
  cursor: pointer;
}

.time-info,
.driver-phone,
.consignee,
.sender {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  line-height: 1.5;
  // color: var(--text-color);
  color: #4d5460;
  gap: 0 8px;
  padding: 4px 0;
}

.time-info div:first-child,
.driver-phone::before,
.consignee::before,
.sender::before {
  // color: var(--text-color);
  color: #4d5460;
  font-weight: 400;
  width: 110px;
  flex-shrink: 0;
}

.time-info div:last-child,
.driver-phone span,
.consignee span,
.sender span {
  // color: var(--text-color);
  color: #4d5460;
  font-weight: 600;
  text-align: left;
  flex-grow: 1;
}

.divider {
  height: 1px;
  background-color: #4d546012;
}

.driver-phone::before {
  content: "Водитель";
}
.consignee::before {
  width: 130px;
  font-size: 14px;
}

.sender::before {
  content: "Перевозчик";
}

.time-info,
.driver-phone,
.consignee,
.sender {
  font-size: 14px;
}
</style>
